import React from 'react';

interface SubscriptionConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  creatorName: string;
  subscriptionType: 'monthly' | 'annual';
  totalAmount: number | undefined;
  creatorAmount: number | undefined;
  platformAmount: number | undefined;
  creatorPercentage: number | undefined;
  platformPercentage: number | undefined;
  isProcessing?: boolean;
}

const SubscriptionConfirmModal: React.FC<SubscriptionConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  creatorName,
  subscriptionType,
  totalAmount,
  creatorAmount,
  platformAmount,
  creatorPercentage,
  platformPercentage,
  isProcessing = false
}) => {
  if (!isOpen) return null;

  const formatAmount = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null || isNaN(Number(amount))) {
      return '0.0000';
    }
    return Number(amount).toFixed(4);
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-50" 
        style={{ zIndex: 1050 }}
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div 
        className="position-fixed top-50 start-50 translate-middle" 
        style={{ zIndex: 1051, width: '90%', maxWidth: '500px' }}
      >
        <div className="card shadow-lg border-0 rounded-xxl">
          {/* Header */}
          <div className="card-header border-0 p-4 pb-2">
            <div className="d-flex align-items-center justify-content-between">
              <h4 className="fw-700 font-lg text-grey-900 mb-0">
                Confirm Subscription
              </h4>
              <button
                type="button"
                className="btn-close bg-transparent border-0 p-0"
                onClick={onClose}
                disabled={isProcessing}
              >
                <i className="ti-close font-xs text-grey-900"></i>
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="card-body p-4 pt-2">
            {/* Creator Info */}
            <div className="text-center mb-4">
              <h5 className="fw-600 text-grey-900 mb-1">
                Subscribe to {creatorName}
              </h5>
              <p className="font-xsss text-grey-500 mb-0">
                {subscriptionType === 'monthly' ? 'Monthly' : 'Annual'} subscription
              </p>
            </div>

            {/* Payment Breakdown */}
            <div className="bg-greylight rounded-3 p-3 mb-4">
              <div className="row">
                <div className="col-12 mb-3">
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="fw-600 text-grey-900 font-xss">
                      Total Amount
                    </span>
                    <span className="fw-700 text-grey-900 font-xs">
                      {formatAmount(totalAmount)} SOL
                    </span>
                  </div>
                </div>

                <div className="col-12">
                  <div className="border-top pt-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center">
                        <i className="feather-user text-primary me-2 font-xss"></i>
                        <span className="font-xsss text-grey-700">
                          {formatAmount(creatorAmount)} SOL ({creatorPercentage}%) goes to creator
                        </span>
                      </div>
                    </div>
                    
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center">
                        <i className="feather-settings text-grey-500 me-2 font-xss"></i>
                        <span className="font-xsss text-grey-700">
                          {formatAmount(platformAmount)} SOL ({platformPercentage}%) goes to platform
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Warning */}
            <div className="bg-warning-light p-3 rounded-3 mb-4">
              <div className="d-flex">
                <i className="feather-info text-warning me-2 mt-1 font-xs"></i>
                <div>
                  <p className="font-xsss text-warning fw-600 mb-1">
                    Transaction Confirmation
                  </p>
                  <p className="font-xsss text-grey-700 mb-0">
                    This will create a Solana blockchain transaction. Make sure you have enough SOL in your wallet to cover the subscription cost plus network fees.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="row">
              <div className="col-6">
                <button
                  type="button"
                  className="btn bg-greylight text-grey-900 font-xsss fw-600 p-3 rounded-3 w-100"
                  onClick={onClose}
                  disabled={isProcessing}
                >
                  Cancel
                </button>
              </div>
              <div className="col-6">
                <button
                  type="button"
                  className="btn bg-current text-white font-xsss fw-600 p-3 rounded-3 w-100"
                  onClick={onConfirm}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Processing...</span>
                      </div>
                      Processing...
                    </>
                  ) : (
                    'Confirm & Pay'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SubscriptionConfirmModal;